import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Package, Zap, DollarSign, ShoppingBag, Globe, Link2, LogOut, Users, AlertTriangle, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import ProductForm from '@/components/admin/ProductForm';
import ProductList from '@/components/admin/ProductList';
import OrderList from '@/components/admin/OrderList';
import { useAuth } from '@/context/AuthContext';
import { StockAlertWidget } from '@/pages/StockAlertsPage';
import { FulfillmentWidget } from '@/pages/FulfillmentPage';
import { PushPromoCard } from '@/components/admin/PushPromoCard';
import { StoreWhatsAppCard } from '@/components/admin/StoreWhatsAppCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const adminAuthHeaders = () => {
  const token = localStorage.getItem('salesspace_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EMPTY_PRODUCT = {
  name: '', description: '', price: '', category: '', image_url: '',
  images: [],  // Additional product images for gallery
  videos: [],
  product_type: 'physical', affiliate_url: '', stock: '', commission_rate: '',
  featured: false, tags: '',
  // Variant fields
  has_variants: false, available_sizes: [], available_colors: []
};

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, colorClass }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClass}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Stats Grid Component
const StatsGrid = ({ stats }) => {
  if (!stats) return null;
  
  const statItems = [
    { icon: Package, label: 'Physical Products', value: stats.physical_products, colorClass: 'bg-[#2563EB]/10 text-[#2563EB]' },
    { icon: Zap, label: 'Affiliate Products', value: stats.affiliate_products, colorClass: 'bg-[#F97316]/10 text-[#F97316]' },
    { icon: ShoppingBag, label: 'Paid Orders', value: stats.paid_orders, colorClass: 'bg-[#22c55e]/10 text-[#22c55e]' },
    { icon: DollarSign, label: 'Total Revenue', value: `$${stats.total_revenue?.toFixed(2) || '0.00'}`, colorClass: 'bg-secondary' }
  ];
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      {statItems.map((item) => (
        <StatCard key={item.label} {...item} />
      ))}
    </div>
  );
};

// Custom hook for admin data
const useAdminData = () => {
  const { getAuthHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const authHeaders = getAuthHeader();
      const [productsRes, ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/products?limit=100`),
        axios.get(`${API}/orders?limit=50`),
        axios.get(`${API}/admin/stats`, { headers: authHeaders, withCredentials: true })
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { products, orders, stats, loading, refetch: fetchData };
};

// Custom hook for product form
const useProductForm = (onSuccess) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  const openDialog = useCallback((product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        ...product,
        tags: product.tags?.join(', ') || '',
        stock: product.stock?.toString() || '',
        commission_rate: product.commission_rate?.toString() || '',
        // Variant fields
        has_variants: product.has_variants || false,
        available_sizes: product.available_sizes || [],
        available_colors: product.available_colors || []
      });
    } else {
      setEditingProduct(null);
      setFormData(EMPTY_PRODUCT);
    }
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingProduct(null);
    setFormData(EMPTY_PRODUCT);
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        image_url: formData.image_url,
        images: formData.images || [],  // Additional gallery images
        videos: formData.videos || [],
        product_type: formData.product_type,
        affiliate_url: formData.product_type === 'affiliate' ? formData.affiliate_url : null,
        stock: formData.product_type === 'physical' ? parseInt(formData.stock) || 0 : 0,
        commission_rate: formData.product_type === 'affiliate' ? parseFloat(formData.commission_rate) || null : null,
        featured: formData.featured,
        tags: formData.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean),
        // Variant fields
        has_variants: formData.has_variants || false,
        available_sizes: formData.available_sizes || [],
        available_colors: formData.available_colors || []
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, { headers: adminAuthHeaders() });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, payload, { headers: adminAuthHeaders() });
        toast.success('Product created successfully');
      }

      closeDialog();
      onSuccess();
    } catch (err) {
      console.error('Error saving product:', err);
      toast.error(err.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  }, [formData, editingProduct, closeDialog, onSuccess]);

  return {
    dialogOpen, setDialogOpen, editingProduct, formData, saving,
    openDialog, closeDialog, handleFormChange, handleSubmit
  };
};

// Custom hook for product actions
const useProductActions = (refetch) => {
  const handleDelete = useCallback(async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { headers: adminAuthHeaders() });
      toast.success('Product deleted');
      refetch();
    } catch (err) {
      console.error('Error deleting product:', err);
      toast.error('Failed to delete product');
    }
  }, [refetch]);

  const handleUpdateOrderStatus = useCallback(async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${status}`, null, { headers: adminAuthHeaders() });
      toast.success('Order status updated');
      refetch();
    } catch (err) {
      console.error('Error updating order:', err);
      toast.error('Failed to update order');
    }
  }, [refetch]);

  return { handleDelete, handleUpdateOrderStatus };
};

const AdminPage = () => {
  const { products, orders, stats, loading, refetch } = useAdminData();
  const productForm = useProductForm(refetch);
  const { handleDelete, handleUpdateOrderStatus } = useProductActions(refetch);
  const { logout, user } = useAuth();

  const productCount = useMemo(() => products.length, [products]);
  const orderCount = useMemo(() => orders.length, [orders]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-slide-up">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Manage your products and orders</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground hidden md:block">
            {user?.email}
          </span>
          <Dialog open={productForm.dialogOpen} onOpenChange={productForm.setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" onClick={() => productForm.openDialog()} data-testid="add-product-btn">
                <Plus className="w-4 h-4" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Archivo, sans-serif' }}>
                  {productForm.editingProduct ? 'Edit Product' : 'Add New Product'}
                </DialogTitle>
              </DialogHeader>
              <ProductForm 
                formData={productForm.formData}
                onChange={productForm.handleFormChange}
                onSubmit={productForm.handleSubmit}
                onCancel={productForm.closeDialog}
                saving={productForm.saving}
                isEditing={!!productForm.editingProduct}
              />
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="icon" onClick={handleLogout} title="Logout">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Quick Links to Import Pages */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Link to="/admin/merchants" className="group">
          <Card className="transition-all hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Merchant Management</h3>
                  <p className="text-sm text-muted-foreground">Approve and manage marketplace merchants</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/alibaba" className="group">
          <Card className="transition-all hover:border-orange-500/50 hover:shadow-lg hover:shadow-orange-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center group-hover:bg-orange-500 group-hover:text-white transition-colors">
                  <Globe className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Alibaba Dropship Import</h3>
                  <p className="text-sm text-muted-foreground">Import products from Alibaba suppliers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/temu" className="group">
          <Card className="transition-all hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-500/10 text-purple-500 flex items-center justify-center group-hover:bg-purple-500 group-hover:text-white transition-colors">
                  <Link2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Temu Affiliate Products</h3>
                  <p className="text-sm text-muted-foreground">Add Temu products via CJ/ShareASale</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/commissions" className="group">
          <Card className="transition-all hover:border-green-500/50 hover:shadow-lg hover:shadow-green-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 text-green-500 flex items-center justify-center group-hover:bg-green-500 group-hover:text-white transition-colors">
                  <DollarSign className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Affiliate Commissions</h3>
                  <p className="text-sm text-muted-foreground">Track clicks & manage 15% payouts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/stock-alerts" className="group">
          <Card className="transition-all hover:border-orange-500/50 hover:shadow-lg hover:shadow-orange-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center group-hover:bg-orange-500 group-hover:text-white transition-colors">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Stock Alerts</h3>
                  <p className="text-sm text-muted-foreground">Monitor low inventory levels</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/fulfillment" className="group">
          <Card className="transition-all hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-cyan-500/10 text-cyan-500 flex items-center justify-center group-hover:bg-cyan-500 group-hover:text-white transition-colors">
                  <Truck className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Order Fulfillment</h3>
                  <p className="text-sm text-muted-foreground">Ship orders & track deliveries</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Alert Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StockAlertWidget />
        <FulfillmentWidget />
        <PushPromoCard />
        <StoreWhatsAppCard />
      </div>

      <StatsGrid stats={stats} />

      <Tabs defaultValue="products">
        <TabsList className="mb-6">
          <TabsTrigger value="products" className="gap-2" data-testid="tab-products">
            <Package className="w-4 h-4" />
            Products ({productCount})
          </TabsTrigger>
          <TabsTrigger value="orders" className="gap-2" data-testid="tab-orders">
            <ShoppingBag className="w-4 h-4" />
            Orders ({orderCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <ProductList products={products} loading={loading} onEdit={productForm.openDialog} onDelete={handleDelete} />
        </TabsContent>

        <TabsContent value="orders">
          <OrderList orders={orders} loading={loading} onUpdateStatus={handleUpdateOrderStatus} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
